import React, { useMemo } from 'react';

import { defaultStyles } from '@visx/tooltip';
import {
  AnimatedBarSeries,
  XYChart,
  Tooltip,
  AnimatedLineSeries,
} from '@visx/xychart';
import { FormattedMessage } from 'react-intl';

import { PipelinesTimeseries } from 'src/components/pipelines/hooks/use-pipelines-timeseries';
import { PipelinesTimeseriesAverage } from 'src/components/pipelines/hooks/use-pipelines-timeseries-average';

import { PipelinesTimeseriesDataWithIndex } from '../../types';
import { ChangedValue } from '../metric-cards/changed-value';
import { ChartValue } from '../metric-cards/chart-value';
import { messages } from '../metric-cards/i18n';
import { Description, tooltipStyles } from '../metric-cards/styles';

import { AverageChartInfo } from './average-chart-info';
import { AverageLineSeries } from './average-line-series';
import { ChartContainer } from './chart-container';
import { ChartTooltip } from './chart-tooltip';
import { ChartWrapper } from './chart-wrapper';
import { ColorAccessorParams } from './utils';

type ChartConfig = {
  dataKey: string;
  colorAccessor?: (p: ColorAccessorParams) => string;
};

type TooltipConfig = {
  dataAccessor: (
    data: PipelinesTimeseriesDataWithIndex['data']
  ) => number | string;
  label: string;
};

type AverageChartProps = PipelinesTimeseriesAverage &
  PipelinesTimeseries & {
    chartConfig: ChartConfig[];
    showLastWeekValue?: boolean;
    yScaleZero?: boolean;
    chartType?: 'bar' | 'line';
    tooltipConfig: TooltipConfig[];
    opinionated?: boolean;
    dataFormatter?: (data?: number) => string;
    chartTitle: string;
    chartValueComparator: (x: number, y: number) => boolean;
  };

type TooltipData = {
  nearestDatum: {
    datum: PipelinesTimeseriesDataWithIndex;
  };
};

/* eslint @typescript-eslint/ban-types: "warn" */
export const AverageChart: React.FC<AverageChartProps> = ({
  data,
  lastWeekValue,
  difference,
  average,
  standardDeviation,
  isLoading,
  error,
  showLastWeekValue = true,
  yScaleZero = true,
  chartType = 'bar',
  chartConfig,
  tooltipConfig,
  opinionated = true,
  chartTitle,
  dataFormatter = d => d?.toLocaleString() || '',
  chartValueComparator,
}) => {
  const { hasDifference, isDifferenceIncreased, isDifferenceImproved } =
    useMemo(() => {
      return {
        hasDifference: difference !== 0,
        isDifferenceIncreased: difference > 0,
        isDifferenceImproved: chartValueComparator(difference, 0),
      };
    }, [difference, chartValueComparator]);

  const getTooltip = (tooltipData: TooltipData) => {
    const { datum } = tooltipData.nearestDatum;
    return (
      <ChartTooltip
        dataAvailable={!!datum.data}
        startAt={datum.startAt}
        tooltips={tooltipConfig.map(({ dataAccessor, label }) => {
          return {
            label,
            value: dataAccessor(datum.data),
          };
        })}
      />
    );
  };

  const ChartLastWeekValue = () => {
    if (!opinionated)
      return <ChartValue value={dataFormatter(lastWeekValue)} />;

    return (
      <ChangedValue
        showCurrentValue={showLastWeekValue}
        value={dataFormatter(lastWeekValue)}
        difference={dataFormatter(difference)}
        hasDifference={hasDifference}
        isDifferenceImproved={isDifferenceImproved}
        isDifferenceIncreased={isDifferenceIncreased}
      />
    );
  };

  const ChartInfo = () => {
    if (lastWeekValue === undefined) {
      return (
        <Description>
          <FormattedMessage {...messages.noDataLastWeek} />
        </Description>
      );
    }

    if (!opinionated) return null;

    return (
      <AverageChartInfo
        chartTitle={chartTitle}
        difference={dataFormatter(difference)}
        hasLastWeekValue={lastWeekValue !== undefined}
        hasDifference={hasDifference}
        isDifferenceImproved={isDifferenceImproved}
        isDifferenceIncreased={isDifferenceIncreased}
      />
    );
  };

  return (
    <ChartWrapper isLoading={isLoading} error={error} chartTitle={chartTitle}>
      <ChartLastWeekValue />
      <ChartContainer>
        {({ width, height }) => (
          <XYChart
            height={height}
            width={width}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            xScale={{ type: 'band', paddingInner: 0.2 }}
            yScale={{ type: 'linear', zero: yScaleZero }}
          >
            {chartConfig.map(({ dataKey, colorAccessor }) => {
              const commonChartProps = {
                dataKey: `${chartTitle}${dataKey}`,
                data,
                xAccessor: (d: PipelinesTimeseriesDataWithIndex) => d.index,
                yAccessor: (d: PipelinesTimeseriesDataWithIndex) =>
                  d.data?.[dataKey],
              };
              if (chartType === 'line')
                return (
                  <AnimatedLineSeries key={dataKey} {...commonChartProps} />
                );
              return (
                <AnimatedBarSeries
                  key={dataKey}
                  {...commonChartProps}
                  colorAccessor={(d: PipelinesTimeseriesDataWithIndex) =>
                    colorAccessor &&
                    colorAccessor({
                      value: d.data?.[dataKey],
                      average,
                      averageComparator: chartValueComparator,
                      standardDeviation,
                    })
                  }
                />
              );
            })}
            <AverageLineSeries
              chartTitle={chartTitle}
              display={hasDifference}
              average={average}
            />
            <style>
              {`.visx-tooltip {
                  z-index: 9999;
                }`}
            </style>
            <Tooltip
              snapTooltipToDatumX
              snapTooltipToDatumY
              showVerticalCrosshair
              showSeriesGlyphs
              style={{
                ...defaultStyles,
                ...tooltipStyles,
              }}
              renderTooltip={({ tooltipData }) =>
                getTooltip(tooltipData as TooltipData)
              }
            />
          </XYChart>
        )}
      </ChartContainer>
      <ChartInfo />
    </ChartWrapper>
  );
};
